import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HandCoins, Landmark, GraduationCap, Globe, ExternalLink, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import ScholarshipsCard from "./ScholarshipsCard";

/**
 * TabScholarships — dedicated scholarship / funding tab.
 *
 * Sections:
 *   01 · What kinds of funding exist    (editorial primer)
 *   02 · Scholarships at this uni       (curated + scraped list)
 *   03 · National & external schemes    (country-anchored list of
 *                                        Chevening / Fulbright / Rhodes / etc)
 *
 * Country-anchored external schemes are hard-coded here rather than
 * fetched — they change once every few years and we want zero fetch
 * latency. UK/US covered; other countries fall through to a generic
 * pointer.
 */

const NATIONAL_SCHEMES = {
  UK: [
    {
      name: "Chevening Scholarships",
      description: "Fully-funded one-year Masters at any UK uni, awarded by the Foreign, Commonwealth & Development Office. ~1,700 awards per cycle from 160+ countries.",
      audience: "International postgrads",
      amount_usd: 45000,
      deadline_hint: "Sept–Nov cycle · applications close early November",
      source_url: "https://www.chevening.org/",
    },
    {
      name: "Commonwealth Scholarships",
      description: "For students from Commonwealth countries pursuing Masters or PhDs in the UK. Funded by the FCDO.",
      audience: "Commonwealth postgrads",
      amount_usd: 40000,
      deadline_hint: "Autumn cycle · deadlines vary by country",
      source_url: "https://cscuk.fcdo.gov.uk/scholarships/",
    },
    {
      name: "Rhodes Scholarship",
      description: "Postgraduate award at Oxford — one of the most competitive in the world. ~100 per cycle across a curated list of countries.",
      audience: "Oxford postgrads only",
      amount_usd: 70000,
      deadline_hint: "July–Sept national-committee windows",
      source_url: "https://www.rhodeshouse.ox.ac.uk/scholarships/the-rhodes-scholarship/",
    },
    {
      name: "GREAT Scholarships",
      description: "British Council-managed scheme — 300+ awards worth £10,000 each toward Masters study at partner UK universities.",
      audience: "International postgrads · 15 partner countries",
      amount_usd: 12000,
      deadline_hint: "Feb–May cycle",
      source_url: "https://study-uk.britishcouncil.org/scholarships/great-scholarships",
    },
    {
      name: "Marshall Scholarship",
      description: "US citizens funded to study in the UK for 1-2 years. ~50 awards annually.",
      audience: "US undergrads → UK postgrad",
      amount_usd: 65000,
      deadline_hint: "US application window closes late Sept",
      source_url: "https://www.marshallscholarship.org/",
    },
    {
      name: "Student Finance England / SAAS / SFW / SFNI",
      description: "Home-fee students only — tuition-fee loan + means-tested maintenance loan. Cover the sticker price for undergrad UK residents.",
      audience: "UK-domiciled undergrads",
      amount_usd: null,
      deadline_hint: "Applications open mid-February · rolling",
      source_url: "https://www.gov.uk/student-finance",
    },
  ],
  US: [
    {
      name: "Fulbright Program",
      description: "US Department of State exchange scholarships — 2,000+ US students study abroad each year; 4,000+ international students come to the US. Fully funded.",
      audience: "Postgrad · both directions",
      amount_usd: 40000,
      deadline_hint: "US applications close early October",
      source_url: "https://us.fulbrightonline.org/",
    },
    {
      name: "Pell Grant",
      description: "Federal need-based grant for US undergrads with demonstrated financial need. Does not need to be repaid.",
      audience: "US-domiciled undergrads",
      amount_usd: 7400,
      deadline_hint: "FAFSA opens Oct 1 · priority by mid-March",
      source_url: "https://studentaid.gov/understand-aid/types/grants/pell",
    },
    {
      name: "Federal Direct Loans (FAFSA)",
      description: "Subsidised + unsubsidised federal loans — the primary funding tool for most US undergrads. Better terms than private loans.",
      audience: "US-domiciled undergrads",
      amount_usd: null,
      deadline_hint: "FAFSA opens Oct 1 annually",
      source_url: "https://studentaid.gov/h/apply-for-aid/fafsa",
    },
    {
      name: "Rhodes Scholarship (US → Oxford)",
      description: "US citizens funded for Oxford postgraduate study. ~32 US awards per cycle.",
      audience: "US undergrads → Oxford postgrad",
      amount_usd: 70000,
      deadline_hint: "US applications close early October",
      source_url: "https://www.rhodesscholar.org/",
    },
    {
      name: "Truman Scholarship",
      description: "For US juniors committed to public-service careers — $30k for grad school + leadership development.",
      audience: "US undergrad juniors",
      amount_usd: 30000,
      deadline_hint: "Campus deadlines vary · February for finalists",
      source_url: "https://www.truman.gov/",
    },
  ],
};

const CATEGORY_PRIMER = [
  {
    icon: Landmark,
    title: "University scholarships",
    detail: "Awards funded directly by the university, decided by admissions or the college. Usually merit-based, sometimes need-based, and often applied to automatically when you accept an offer.",
  },
  {
    icon: GraduationCap,
    title: "Course / department scholarships",
    detail: "Specific to one degree programme — funded by the department, alumni gifts, or corporate partnerships. Ask the admissions tutor once you have an offer.",
  },
  {
    icon: Globe,
    title: "National & external schemes",
    detail: "Chevening, Fulbright, Rhodes, Commonwealth Scholarships and similar — funded by governments, foundations, or third parties. Applied for separately, usually by early autumn.",
  },
  {
    icon: HandCoins,
    title: "Bursaries & hardship funds",
    detail: "Need-based support once you're enrolled — smaller amounts but easier to secure. Every uni runs one; check the student-services page after matriculation.",
  },
];

export default function TabScholarships({ uni }) {
  const country = uni.country;
  const nationalRows = NATIONAL_SCHEMES[country] || [];
  const fmt = (v) => (v ? `$${v.toLocaleString()}` : "—");

  return (
    <section className="pt-8 space-y-10" data-testid="tab-scholarships">
      {/* 01 · What kinds of funding exist */}
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2">
          01 · What kinds of funding exist
        </div>
        <h2 className="font-editorial text-2xl md:text-3xl text-[#002147] leading-tight border-b border-[#002147]/10 pb-4 mb-6">
          Four routes to pay less than the sticker price
        </h2>
        <div className="max-w-3xl">
          <p className="font-serif text-base leading-relaxed text-[#002147]/85 mb-6">
            The sticker price on the Cost tab is what you pay if you self-fund
            without any support. Most students pay less — sometimes far less —
            once scholarships, bursaries, and external schemes are stacked on
            top. Broadly, funding falls into four categories, and it's normal
            to combine two or three of them across your degree.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CATEGORY_PRIMER.map(({ icon: Icon, title, detail }) => (
              <div key={title} className="border border-[#002147]/10 bg-white/40 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4 text-[#7C3AED]" />
                  <div className="font-serif text-base text-[#002147]">{title}</div>
                </div>
                <div className="font-sans text-sm text-[#002147]/75 leading-relaxed">
                  {detail}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 02 · Scholarships at this uni — existing list component */}
      <div>
        <ScholarshipsCard uniId={uni.id} />
      </div>

      {/* 03 · National & external schemes */}
      {nationalRows.length > 0 && (
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#7C3AED] mb-2">
            03 · National & External Schemes
          </div>
          <h2 className="font-editorial text-2xl md:text-3xl text-[#002147] leading-tight border-b border-[#002147]/10 pb-4 mb-6">
            {country === "UK" ? "Big schemes you should look at for a UK degree" : "Big schemes you should look at for a US degree"}
          </h2>
          <div className="border border-[#002147]/10 bg-white/40 divide-y divide-[#002147]/10">
            {nationalRows.map((s, i) => (
              <div key={s.name} className="p-5" data-testid={`national-scheme-${i}`}>
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#002147] leading-tight">{s.name}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
                      {s.audience}
                    </div>
                  </div>
                  {s.amount_usd && (
                    <div className="text-right shrink-0">
                      <div className="font-serif text-xl text-[#002147] leading-none">
                        {fmt(s.amount_usd)}
                      </div>
                      <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 mt-1">
                        approx / yr
                      </div>
                    </div>
                  )}
                </div>
                <p className="font-sans text-sm text-[#002147]/80 leading-relaxed mb-3">
                  {s.description}
                </p>
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-[#B45309]">
                    {s.deadline_hint}
                  </div>
                  <a
                    href={s.source_url}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline"
                  >
                    Scheme site <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 04 · Editorial closer */}
      <div className="border-t border-[#002147]/10 pt-6">
        <div className="flex items-start gap-3 max-w-3xl">
          <Sparkles className="h-4 w-4 mt-1 shrink-0 text-[#7C3AED]" />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#002147]/60 mb-2">
              How to actually apply
            </div>
            <p className="font-serif text-base leading-relaxed text-[#002147]/80">
              University scholarships are almost always automatic — accept your
              offer and the awards fire behind the scenes. National schemes are
              the opposite: apply separately, months earlier, with essays and
              references. Bursaries you apply for once you arrive. Stack them.
              Ask the admissions office about anything specific to your subject —
              those are the ones nobody advertises.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
